import React, { memo, useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import { MY_DAY_CONFIRMATION_KEY } from '../../../common/constants';
import {
	DOCKET_ACCESS_REQUIREMENT_TYPE,
	getDocketAccessRequirements,
} from '../../../common/utils/myDay/driverDaySteps';
import Text from '../../components/text/Text';
import { ActionDock, Card, EmptyState, MachineChip, TickRow } from '../../design/components';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { groupAccessItems } from '../../utils/groupAccessItems';
import AccessItemStops from './AccessItemStops';
import { useDriverDay } from './DriverDayProvider';
import StartOfDayStepScreen from './startOfDay/StartOfDayStepScreen';
import useStartOfDayStep from './startOfDay/useStartOfDayStep';
import useDriverDayActions from './useDriverDayActions';

const ITEM_OVERLINE = {
	[DOCKET_ACCESS_REQUIREMENT_TYPE.KEY]: 'KEY / FOB',
	[DOCKET_ACCESS_REQUIREMENT_TYPE.ACCESS_PASS]: 'ACCESS PASS',
	[DOCKET_ACCESS_REQUIREMENT_TYPE.EQUIPMENT]: 'PPE / EQUIPMENT',
};

const countLabel = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

const AccessStopCard = memo(({ row, tickedKeys, disabled, onToggle }) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const { docket, stopNumber } = row;
	const location = docket.machine?.location;
	const address = [location?.addressObj?.street, location?.addressObj?.city].filter(Boolean).join(', ');
	const items = row.items.filter(requirement => requirement.type !== DOCKET_ACCESS_REQUIREMENT_TYPE.SIGN_IN);
	const signIns = row.items.filter(requirement => requirement.type === DOCKET_ACCESS_REQUIREMENT_TYPE.SIGN_IN);

	const showStopHeading = items.every(item => item.stops.length === 1);

	return (
		<Card style={styles.card}>
			{showStopHeading && <View style={styles.cardHeading}>
				<Text style={styles.siteName}>{location?.name || row.stopName}</Text>
				<View style={styles.positionRow}>
					{Boolean(docket.machine?.place?.name) && <Text style={styles.position}>{docket.machine.place.name}</Text>}
					<MachineChip number={docket.machine?.assetId} />
				</View>
				<Text style={styles.address}>{[address, `Stop ${stopNumber}`].filter(Boolean).join(' · ')}</Text>
			</View>}
			{items.map(requirement => (
				<View key={requirement.id} style={styles.cardHeading}>
					<TickRow
						overline={ITEM_OVERLINE[requirement.type]}
						title={requirement.value || requirement.label}
						checked={requirement.tickKeys.every(key => tickedKeys.has(key))}
						disabled={disabled}
						onToggle={() => onToggle(requirement.tickKeys)}
					/>
					{!showStopHeading && <AccessItemStops stops={requirement.stops} />}
				</View>
			))}
			{signIns.map(requirement => (
				<View key={requirement.label} style={styles.signIn}>
					<Feather name="log-in" size={18} color={colors.onChip} />
					<Text style={styles.signInText}>
						<Text style={styles.signInLabel}>{!showStopHeading ? `${location?.name || 'Site'} sign-in: ` : 'Site sign-in: '}</Text>
						{requirement.value || 'Sign in at the site when you arrive.'}
					</Text>
				</View>
			))}
		</Card>
	);
});

/**
 * Keys and site access (start of day step): only the stops that need a key or
 * FOB, an access pass, PPE or a site sign-in. Items tick off as a packing aid;
 * "Keys packed and access reviewed" confirms the step with the ticked items,
 * so managers see what was not packed.
 */
const KeysAccessView = () => {
	const day = useDriverDay();
	const { accessKeyRows, sortedDockets, trainingOffline } = day;
	const { isDone, stepLabel, afterThis } = useStartOfDayStep(day, 'accessKeys');
	const { confirmStepByKey, isConfirmingStep } = useDriverDayActions(day);
	const [tickedKeys, setTickedKeys] = useState(() => new Set());

	const rows = useMemo(() => groupAccessItems(accessKeyRows.map(row => ({
		...row,
		stopNumber: sortedDockets.findIndex(docket => docket.id === row.id) + 1,
		items: getDocketAccessRequirements(row.docket).map(requirement => ({
			...requirement,
			id: `${requirement.scope}:${requirement.type}`,
		})),
	}))), [accessKeyRows, sortedDockets]);
	const itemCount = rows.reduce((sum, row) => (
		sum + row.items.filter(requirement => requirement.type !== DOCKET_ACCESS_REQUIREMENT_TYPE.SIGN_IN).length
	), 0);
	const otherStopCount = sortedDockets.length - accessKeyRows.length;

	const toggleItem = useCallback((itemKeys) => {
		setTickedKeys((current) => {
			const next = new Set(current);
			const checked = itemKeys.every(key => current.has(key));
			itemKeys.forEach(key => (checked ? next.delete(key) : next.add(key)));
			return next;
		});
	}, []);

	// Once confirmed, show the saved ticks (on any phone); older confirmations have no list.
	const savedItems = day.serviceRun?.myDayConfirmations
		?.find(confirmation => confirmation?.key === MY_DAY_CONFIRMATION_KEY.ACCESS_KEYS)?.items;
	const displayedKeys = isDone && Array.isArray(savedItems) ? new Set(savedItems) : tickedKeys;

	const packedCount = rows.reduce((sum, row) => sum + row.items.filter(item => (
		item.type !== DOCKET_ACCESS_REQUIREMENT_TYPE.SIGN_IN && item.tickKeys.every(key => displayedKeys.has(key))
	)).length, 0);
	const packedHint = itemCount ? `${packedCount} of ${countLabel(itemCount, 'item', 'items')} packed` : afterThis;

	return (
		<StartOfDayStepScreen
			day={day}
			stepLabel={stepLabel}
			heading="Keys and site access"
			subheading="Pack these before you leave. Only stops that need something are listed."
			dock={isDone ? (
				<ActionDock hint="Keys and site access confirmed." />
			) : (
				<ActionDock
					hint={packedHint}
					action={{
						label: 'Keys packed and access reviewed',
						onPress: () => confirmStepByKey('accessKeys', Array.from(tickedKeys)),
						loading: isConfirmingStep,
						disabled: trainingOffline || !rows.length,
					}}
				/>
			)}
		>
			{rows.map(row => (
				<AccessStopCard key={row.id} row={row} tickedKeys={displayedKeys} disabled={isDone} onToggle={toggleItem} />
			))}
			{!rows.length && (
				<EmptyState title="Nothing to pack" message="No stop on this run needs keys, passes or PPE." />
			)}
			{rows.length > 0 && otherStopCount > 0 && (
				<FootNote text={`The other ${countLabel(otherStopCount, 'stop needs', 'stops need')} no keys, passes or PPE.`} />
			)}
		</StartOfDayStepScreen>
	);
};

const FootNote = ({ text }) => {
	const styles = useThemedStyles(createStyles);
	return <Text style={styles.footNote}>{text}</Text>;
};

const createStyles = ({ ui: { colors, radius, space, type } }) => ({
	card: {
		gap: space.md,
	},
	cardHeading: {
		gap: space.xs,
	},
	siteName: {
		...type.sectionTitle,
		color: colors.text,
	},
	positionRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: space.sm,
	},
	position: {
		...type.bodySmallStrong,
		color: colors.text,
	},
	address: {
		...type.caption,
		color: colors.textSecondary,
	},
	signIn: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: space.sm,
		padding: space.md,
		borderRadius: radius.control,
		backgroundColor: colors.chip,
	},
	signInText: {
		...type.secondary,
		color: colors.onChip,
		flex: 1,
	},
	signInLabel: {
		...type.secondarySemibold,
		color: colors.onChip,
	},
	footNote: {
		...type.caption,
		color: colors.textSecondary,
	},
});

export default KeysAccessView;
